import {
  BUILT_IN_DOCX_THEME_NAMES,
  ThemeOverridesSchema
} from "./chunk-OVL4RTD7.js";
import {
  DOCX_RENDERER_IDS,
  VisualPropsSchema,
  docxFlowDefinitionName,
  docxPropsSchemaForRenderer
} from "./chunk-HDFGXHUB.js";
import {
  ColumnsPropsSchema,
  ComponentDefaultsSchema,
  HeadingPropsSchema,
  ImagePropsSchema,
  ListPropsSchema,
  ParagraphPropsSchema,
  SectionPropsSchema,
  StatisticPropsSchema,
  TablePropsSchema,
  createSectionPropsSchema,
  createTablePropsSchema
} from "./chunk-W6KB2SMK.js";
import {
  AlignmentSchema,
  FloatingPropertiesSchema,
  SpacingSchema
} from "./chunk-FYJOHPU2.js";
import {
  HexColorSchema,
  NoProofWordsSchema
} from "./chunk-XRVPNHNS.js";

// src/schemas/component-registry.ts
import { BlockInvocationPropsSchema as BlockInvocationPropsSchema2 } from "@json-to-office/shared";

// src/schemas/components/block.ts
import { Type } from "@sinclair/typebox";
import { BlockInvocationPropsSchema } from "@json-to-office/shared";
var GroupPropsSchema = Type.Object(
  {},
  { additionalProperties: false }
);

// src/schemas/component-registry.ts
import { Type as Type8 } from "@sinclair/typebox";

// src/schemas/components/report.ts
import { Type as Type2 } from "@sinclair/typebox";
import {
  FontRegistrySchema,
  BlockDefinitionsSchema
} from "@json-to-office/shared";
var createReportPropsSchema = (_componentRef) => Type2.Object(
  {
    blocks: Type2.Optional(BlockDefinitionsSchema),
    theme: Type2.Optional(
      Type2.String({
        description: `Theme name to apply (default: "consulting"). Built-ins: ${BUILT_IN_DOCX_THEME_NAMES.join(", ")} (consulting is the house style).`,
        examples: [...BUILT_IN_DOCX_THEME_NAMES],
        default: "consulting"
      })
    ),
    themeOverrides: Type2.Optional(ThemeOverridesSchema),
    qualityProfile: Type2.Optional(
      Type2.String({
        description: "The quality profile validation judges this document by when the caller names none \u2014 a blueprint scaffold writes its archetype\u2019s profile here. A profile names required structure and content; the theme only paints. Built-ins: client-report, technical-report, executive-report, legal-appendix, general (the format default); an unknown name falls back to it.",
        examples: [
          "client-report",
          "technical-report",
          "executive-report",
          "legal-appendix",
          "general"
        ]
      })
    ),
    fontRegistry: Type2.Optional(FontRegistrySchema),
    componentDefaults: Type2.Optional(ComponentDefaultsSchema),
    language: Type2.Optional(
      Type2.String({
        pattern: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$",
        description: `Default document language (BCP-47 tag, e.g. "en-US"). Sets Word's default proofing/spell-check language; individual components can override it locally.`,
        examples: ["en-US", "fr-FR", "de-DE", "it-IT", "es-ES"]
      })
    ),
    noProofWords: Type2.Optional(NoProofWordsSchema),
    trackRevisions: Type2.Optional(
      Type2.Boolean({
        description: "Open the document in track-changes mode: Word marks any further edits as revisions. Set automatically on redline documents produced by the diff engine"
      })
    ),
    metadata: Type2.Optional(
      Type2.Object(
        {
          title: Type2.Optional(
            Type2.String({
              description: "Document title",
              examples: ["Annual Report 2024", "Technical Documentation"]
            })
          ),
          subtitle: Type2.Optional(
            Type2.String({
              description: "Document subtitle"
            })
          ),
          description: Type2.Optional(Type2.String()),
          author: Type2.Optional(Type2.String()),
          company: Type2.Optional(
            Type2.String({
              description: "Company name, written to docProps/custom.xml (Word has no core-property slot for it)"
            })
          ),
          date: Type2.Optional(
            Type2.String({
              description: "Document date used by {DATE}/{DATETIME} placeholders (defaults to the generation timestamp)"
            })
          ),
          version: Type2.Optional(
            Type2.String({
              description: "Document version, written to docProps/custom.xml",
              examples: ["1.0", "2024.3"]
            })
          ),
          tags: Type2.Optional(Type2.Array(Type2.String()))
        },
        {
          description: "Document metadata (title, author, company, version, etc.). Package timestamps (dcterms:created/modified) are not part of this object: they come from the `generatedAt` generation option so repeated builds stay byte-identical.",
          additionalProperties: false
        }
      )
    )
  },
  {
    description: "Report component props",
    additionalProperties: false
  }
);
var ReportPropsSchema = createReportPropsSchema();

// src/schemas/components/text-box.ts
import { Type as Type3 } from "@sinclair/typebox";
var TextBoxPropsSchema = Type3.Object(
  {
    width: Type3.Optional(
      Type3.Union(
        [
          Type3.Number({
            minimum: 1,
            description: "Text box width in pixels"
          }),
          Type3.String({
            pattern: "^\\d+(\\.\\d+)?%$",
            description: 'Text box width as percentage (e.g., "50%") relative to content width'
          })
        ],
        {
          description: 'Text box width in pixels (number) or as percentage string (e.g., "50%")'
        }
      )
    ),
    height: Type3.Optional(
      Type3.Union(
        [
          Type3.Number({
            minimum: 1,
            description: "Text box height in pixels"
          }),
          Type3.String({
            pattern: "^\\d+(\\.\\d+)?%$",
            description: 'Text box height as percentage (e.g., "50%") relative to content height'
          })
        ],
        {
          description: 'Text box height in pixels (number) or as percentage string (e.g., "50%")'
        }
      )
    ),
    renderAs: Type3.Optional(
      Type3.Union([Type3.Literal("table"), Type3.Literal("shape")], {
        description: "Rendering strategy. 'table' (default): borderless one-cell table \u2014 height auto-fits content, per-side borders and percentage widths resolve in Word. 'shape': native Word text box (WPS shape) \u2014 real wrap modes and z-order, but requires explicit width and height (no autofit), a single uniform border, and eagerly-resolved percentage sizes."
      })
    ),
    floating: Type3.Optional(FloatingPropertiesSchema),
    style: Type3.Optional(
      Type3.Object(
        {
          padding: Type3.Optional(
            Type3.Object(
              {
                top: Type3.Optional(Type3.Number({ minimum: 0 })),
                right: Type3.Optional(Type3.Number({ minimum: 0 })),
                bottom: Type3.Optional(Type3.Number({ minimum: 0 })),
                left: Type3.Optional(Type3.Number({ minimum: 0 }))
              },
              { additionalProperties: false }
            )
          ),
          border: Type3.Optional(
            Type3.Object(
              {
                top: Type3.Optional(
                  // Reuse border schema semantics: style/width/color
                  Type3.Object(
                    {
                      style: Type3.Optional(
                        Type3.Union([
                          Type3.Literal("solid"),
                          Type3.Literal("dashed"),
                          Type3.Literal("dotted"),
                          Type3.Literal("double"),
                          Type3.Literal("none")
                        ])
                      ),
                      width: Type3.Optional(Type3.Number({ minimum: 0 })),
                      color: Type3.Optional(HexColorSchema)
                    },
                    { additionalProperties: false }
                  )
                ),
                right: Type3.Optional(
                  Type3.Object(
                    {
                      style: Type3.Optional(
                        Type3.Union([
                          Type3.Literal("solid"),
                          Type3.Literal("dashed"),
                          Type3.Literal("dotted"),
                          Type3.Literal("double"),
                          Type3.Literal("none")
                        ])
                      ),
                      width: Type3.Optional(Type3.Number({ minimum: 0 })),
                      color: Type3.Optional(HexColorSchema)
                    },
                    { additionalProperties: false }
                  )
                ),
                bottom: Type3.Optional(
                  Type3.Object(
                    {
                      style: Type3.Optional(
                        Type3.Union([
                          Type3.Literal("solid"),
                          Type3.Literal("dashed"),
                          Type3.Literal("dotted"),
                          Type3.Literal("double"),
                          Type3.Literal("none")
                        ])
                      ),
                      width: Type3.Optional(Type3.Number({ minimum: 0 })),
                      color: Type3.Optional(HexColorSchema)
                    },
                    { additionalProperties: false }
                  )
                ),
                left: Type3.Optional(
                  Type3.Object(
                    {
                      style: Type3.Optional(
                        Type3.Union([
                          Type3.Literal("solid"),
                          Type3.Literal("dashed"),
                          Type3.Literal("dotted"),
                          Type3.Literal("double"),
                          Type3.Literal("none")
                        ])
                      ),
                      width: Type3.Optional(Type3.Number({ minimum: 0 })),
                      color: Type3.Optional(HexColorSchema)
                    },
                    { additionalProperties: false }
                  )
                )
              },
              { additionalProperties: false }
            )
          ),
          shading: Type3.Optional(
            Type3.Object(
              {
                // Same domain as every other colour prop and as the border
                // colours above: resolveColor() accepts "#RRGGBB" or a theme
                // colour name, and throws on anything else at render time.
                fill: Type3.Optional(HexColorSchema)
              },
              { additionalProperties: false }
            )
          )
        },
        { additionalProperties: false }
      )
    )
  },
  {
    additionalProperties: false,
    /**
     * The same two shape limits `collectTextBoxShapeConflicts` enforces, said
     * in JSON Schema so an editor can say it too.
     *
     * The deep validator only runs when a document is generated, so until now
     * an author learned that a shape needs a height by pressing Run. These are
     * plain cross-field rules, and `if`/`then` expresses them — which is what
     * Monaco checks the buffer against, so the editor underlines the mistake
     * while it is being made. The deep validator stays as the backstop: it
     * carries the remediation text, and it is what the API enforces for
     * callers who never see an editor.
     */
    allOf: [
      {
        if: {
          properties: { renderAs: { const: "shape" } },
          required: ["renderAs"]
        },
        then: {
          required: ["width", "height"],
          properties: {
            style: {
              properties: {
                border: {
                  properties: Object.fromEntries(
                    ["top", "right", "bottom", "left"].map((side) => [
                      side,
                      { properties: { style: { enum: ["solid", "none"] } } }
                    ])
                  )
                }
              }
            }
          }
        }
      }
    ]
  }
);

// src/schemas/components/toc.ts
import { Type as Type4 } from "@sinclair/typebox";
var TocStyleSchema = Type4.Union(
  [Type4.Literal("numeric"), Type4.Literal("bullet"), Type4.Literal("none")],
  {
    description: "TOC numbering style \u2014 accepted but not applied (Word TOC fields carry no numbering switch); setting it logs a warning during generation"
  }
);
var TocScopeSchema = Type4.Union(
  [Type4.Literal("document"), Type4.Literal("section")],
  { description: "TOC scope: document-wide or section-only" }
);
var TocStyleMappingSchema = Type4.Object(
  {
    styleId: Type4.String({
      description: "Custom style ID matching a key in theme.styles"
    }),
    level: Type4.Number({
      minimum: 1,
      maximum: 6,
      description: "TOC level (1-6) to assign to this style"
    })
  },
  {
    description: "Mapping of custom style to TOC level",
    additionalProperties: false
  }
);
var TocDepthRangeSchema = Type4.Object(
  {
    from: Type4.Optional(
      Type4.Number({
        minimum: 1,
        maximum: 6,
        default: 1,
        description: "Starting heading level (1-6). Defaults to 1 if not specified."
      })
    ),
    to: Type4.Optional(
      Type4.Number({
        minimum: 1,
        maximum: 6,
        default: 3,
        description: "Ending heading level (1-6). Defaults to 3 if not specified."
      })
    )
  },
  {
    description: 'Range of heading levels to include in TOC. Specify at least one of "from" or "to".',
    additionalProperties: false
  }
);
var TocPropsSchema = Type4.Object(
  {
    pageBreak: Type4.Optional(
      Type4.Boolean({
        description: "Insert page break before TOC block"
      })
    ),
    depth: Type4.Optional(
      Type4.Object(
        {
          from: Type4.Optional(
            Type4.Number({
              minimum: 1,
              maximum: 6,
              default: 1,
              description: "Starting heading level (1-6). Defaults to 1 if not specified."
            })
          ),
          to: Type4.Optional(
            Type4.Number({
              minimum: 1,
              maximum: 6,
              default: 3,
              description: "Ending heading level (1-6). Defaults to 3 if not specified."
            })
          )
        },
        {
          description: 'Range of heading levels to include in TOC. Specify "from", "to", or both. Defaults: from=1, to=3',
          additionalProperties: false,
          default: { to: 3 }
        }
      )
    ),
    pageNumbersDepth: Type4.Optional(
      Type4.Object(
        {
          from: Type4.Optional(
            Type4.Number({
              minimum: 1,
              maximum: 6,
              default: 1,
              description: "Starting heading level (1-6). Defaults to 1 if not specified."
            })
          ),
          to: Type4.Optional(
            Type4.Number({
              minimum: 1,
              maximum: 6,
              default: 3,
              description: "Ending heading level (1-6). Defaults to 3 if not specified."
            })
          )
        },
        {
          description: 'Range of heading levels to show page numbers. Specify "from", "to", or both. When specified, page numbers are hidden for entries outside this range.',
          additionalProperties: false
        }
      )
    ),
    numberingStyle: Type4.Optional(TocStyleSchema),
    title: Type4.Optional(
      Type4.String({
        description: "TOC heading title"
      })
    ),
    includePageNumbers: Type4.Optional(
      Type4.Boolean({
        default: true,
        description: "Show page numbers next to entries"
      })
    ),
    numberSeparator: Type4.Optional(
      Type4.Boolean({
        default: true,
        description: 'Use tab separator between entry and page number. True applies "\\t" (default), false applies " "'
      })
    ),
    scope: Type4.Optional(
      Type4.Union([TocScopeSchema, Type4.Literal("auto")], {
        default: "auto",
        description: 'TOC scope: "document" for entire document, "section" for parent section only, "auto" for automatic detection (section if inside section, otherwise document)'
      })
    ),
    styles: Type4.Optional(
      Type4.Array(TocStyleMappingSchema, {
        description: "Custom style mappings for TOC entries. Maps custom theme styles to TOC levels."
      })
    )
  },
  {
    description: "Table of Contents component props",
    additionalProperties: false
  }
);

// src/schemas/components/divider.ts
import { Type as Type5 } from "@sinclair/typebox";
var DividerPropsSchema = Type5.Object(
  {
    thickness: Type5.Optional(
      Type5.Number({
        minimum: 0.25,
        maximum: 12,
        description: "Line weight in points (default 1). OOXML measures a border in eighths of a point and Word stops at 12pt, so this is the whole usable range."
      })
    ),
    color: Type5.Optional(HexColorSchema),
    style: Type5.Optional(
      Type5.Union(
        [
          Type5.Literal("solid"),
          Type5.Literal("dashed"),
          Type5.Literal("dotted"),
          Type5.Literal("double")
        ],
        {
          description: 'Line style (default "solid"). `double` draws two lines and is measured per line, so it reads about twice as tall as its thickness.'
        }
      )
    ),
    width: Type5.Optional(
      Type5.Union(
        [
          Type5.Number({
            minimum: 1,
            description: "Divider width in points"
          }),
          Type5.String({
            pattern: "^\\d+(\\.\\d+)?%$",
            description: 'Divider width as a percentage of the text measure (e.g. "40%")'
          })
        ],
        {
          description: "How far the line runs (default: the full text measure). A percentage or a point width is turned into paragraph indents, resolved against the theme page \u2014 so a partial divider inside a column or a re-margined section is measured against the page, while the default full-measure divider is exact everywhere."
        }
      )
    ),
    alignment: Type5.Optional(
      Type5.Union(
        [Type5.Literal("left"), Type5.Literal("center"), Type5.Literal("right")],
        {
          description: 'Which end of the measure a partial divider sits at (default "left"). Ignored at full width.'
        }
      )
    ),
    spacing: Type5.Optional(SpacingSchema)
  },
  {
    description: "Horizontal divider \u2014 a thin line across the measure, drawn as a Word paragraph border so it stays editable in Word.",
    additionalProperties: false
  }
);

// src/schemas/components/highcharts.ts
import { Type as Type6 } from "@sinclair/typebox";
var HighchartsPropsSchema = Type6.Object({
  // Highcharts chart options - can be anything but must at least have chart.width and chart.height
  options: Type6.Intersect([
    Type6.Record(Type6.String(), Type6.Unknown()),
    Type6.Object({
      chart: Type6.Object({
        width: Type6.Number(),
        height: Type6.Number()
      })
    })
  ]),
  // Optional scale factor for export
  scale: Type6.Optional(Type6.Number()),
  // Optional resources forwarded verbatim to the export server (CSS/JS/files).
  // Notably enables @font-face rules so charts render in custom fonts.
  resources: Type6.Optional(
    Type6.Object({
      css: Type6.Optional(Type6.String()),
      js: Type6.Optional(Type6.String()),
      files: Type6.Optional(Type6.Array(Type6.String()))
    })
  ),
  // Optional Highcharts Export Server URL override
  serverUrl: Type6.Optional(
    Type6.String({
      description: "Highcharts Export Server URL (default: http://localhost:7801)"
    })
  ),
  // Optional width for rendering (overrides chart width)
  width: Type6.Optional(
    Type6.Union(
      [
        Type6.Number({
          minimum: 1,
          description: "Image width in pixels"
        }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'Image width as percentage (e.g., "90%") relative to content width'
        })
      ],
      {
        description: 'Rendered image width in pixels (number) or as percentage string (e.g., "90%")'
      }
    )
  ),
  // Optional height for rendering (overrides chart height)
  height: Type6.Optional(
    Type6.Union(
      [
        Type6.Number({
          minimum: 1,
          description: "Image height in pixels"
        }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: 'Image height as percentage (e.g., "90%") relative to content height'
        })
      ],
      {
        description: 'Rendered image height in pixels (number) or as percentage string (e.g., "90%")'
      }
    )
  )
});

// src/schemas/components/chart.ts
import { Type as Type7 } from "@sinclair/typebox";
var ChartTypeSchema = Type7.Union(
  [
    Type7.Literal("area"),
    Type7.Literal("bar"),
    Type7.Literal("column"),
    Type7.Literal("doughnut"),
    Type7.Literal("line"),
    Type7.Literal("pie"),
    Type7.Literal("radar"),
    Type7.Literal("scatter")
  ],
  { description: "Chart type" }
);
var ChartDataSeriesSchema = Type7.Object(
  {
    name: Type7.Optional(Type7.String({ description: "Series name" })),
    labels: Type7.Optional(
      Type7.Array(Type7.String(), {
        description: "Category labels. Needed on every series, not just the first, and the same length as `values`; a series without both `labels` and `values` drops the whole chart."
      })
    ),
    values: Type7.Optional(
      Type7.Array(Type7.Number(), {
        description: "Data values, one per label. Needed on every series \u2014 see `labels`."
      })
    )
  },
  { additionalProperties: false }
);
var ChartPropsSchema = Type7.Object(
  {
    type: ChartTypeSchema,
    data: Type7.Array(ChartDataSeriesSchema, {
      description: "Chart data series",
      minItems: 1
    }),
    // Title
    title: Type7.Optional(Type7.String({ description: "Chart title text" })),
    showTitle: Type7.Optional(
      Type7.Boolean({
        description: "Show the chart title. Defaults to true when `title` is set."
      })
    ),
    // Legend
    showLegend: Type7.Optional(
      Type7.Boolean({ description: "Show chart legend" })
    ),
    legendPos: Type7.Optional(
      Type7.Union(
        [
          Type7.Literal("b"),
          Type7.Literal("l"),
          Type7.Literal("r"),
          Type7.Literal("t"),
          Type7.Literal("tr")
        ],
        { description: "Legend position" }
      )
    ),
    // Series colors
    chartColors: Type7.Optional(
      Type7.Array(Type7.String(), {
        description: "Series colors (hex or semantic theme names). Defaults to the theme palette."
      })
    ),
    // Axis titles
    catAxisTitle: Type7.Optional(
      Type7.String({ description: "Category axis title" })
    ),
    valAxisTitle: Type7.Optional(
      Type7.String({ description: "Value axis title" })
    ),
    // Flow placement — the same vocabulary as `image` and `highcharts`.
    width: Type7.Optional(
      Type7.Number({
        minimum: 0,
        exclusiveMinimum: 0,
        description: "Chart width in inches. Defaults to the content width."
      })
    ),
    height: Type7.Optional(
      Type7.Number({
        minimum: 0,
        exclusiveMinimum: 0,
        description: "Chart height in inches. Defaults to 3."
      })
    ),
    alignment: Type7.Optional(AlignmentSchema),
    caption: Type7.Optional(
      Type7.String({
        description: "Chart caption (supports rich text with **bold**, *italic*, ***both***)"
      })
    ),
    alt: Type7.Optional(
      Type7.String({ description: "Alternative text for accessibility" })
    ),
    spacing: Type7.Optional(SpacingSchema),
    floating: Type7.Optional(FloatingPropertiesSchema),
    keepNext: Type7.Optional(
      Type7.Boolean({
        description: "Keep paragraph with next paragraph on same page"
      })
    ),
    keepLines: Type7.Optional(
      Type7.Boolean({
        description: "Keep all lines of paragraph together on same page"
      })
    )
  },
  {
    description: 'Native Word chart - editable, scalable, no export server needed. Requires renderer "office-open".',
    additionalProperties: false
  }
);

// src/schemas/component-registry.ts
var FLOW_CHILDREN = [
  "block",
  "group",
  "heading",
  "paragraph",
  "image",
  "statistic",
  "table",
  "list",
  "toc",
  "divider",
  "highcharts",
  "chart",
  "visual",
  "columns",
  "text-box"
];
var STANDARD_COMPONENTS_REGISTRY = [
  // ========================================================================
  // Container Components (can contain children)
  // ========================================================================
  {
    name: "docx",
    propsSchema: ReportPropsSchema,
    hasChildren: true,
    allowedChildren: ["section"],
    category: "container",
    description: "Main document container - defines the overall document structure. Required as the root component.",
    special: {
      hasSchemaField: true
      // Only docx root has $schema field
    }
  },
  {
    name: "section",
    propsSchema: SectionPropsSchema,
    createPropsSchema: createSectionPropsSchema,
    embeddedComponents: [
      // The static schema types both regions as an array of Type.Any() (or
      // the 'linkToPrevious' literal), so the walk is their only structural
      // checker — report malformed entries too.
      { path: ["header"], arity: "component-array", reportStructure: true },
      { path: ["footer"], arity: "component-array", reportStructure: true }
    ],
    hasChildren: true,
    allowedChildren: FLOW_CHILDREN,
    category: "container",
    description: "Section container - groups related content with optional title. Use for organizing document structure."
  },
  {
    name: "columns",
    propsSchema: ColumnsPropsSchema,
    hasChildren: true,
    // Everything in flow but another `columns`: at the top level a columns
    // becomes a section column layout, which has no second level.
    allowedChildren: FLOW_CHILDREN.filter((name) => name !== "columns"),
    category: "layout",
    description: "Multi-column layout - arranges content in 2-4 columns. Great for side-by-side content."
  },
  {
    name: "text-box",
    propsSchema: TextBoxPropsSchema,
    hasChildren: true,
    // A one-cell table, so it holds what a section holds — a table for a
    // metadata band, a columns for a two-up sidebar, another text-box.
    allowedChildren: FLOW_CHILDREN,
    category: "layout",
    description: "Floating text container - allows positioning text anywhere on the page with absolute or relative positioning. Holds any flow content, tables and columns included."
  },
  // ========================================================================
  // Content Components (leaf nodes, no children)
  // ========================================================================
  {
    name: "heading",
    propsSchema: HeadingPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Heading text - supports levels 1-6 for document hierarchy. Level 1 is largest. Never skip a level going down (1 \u2192 3): the gap breaks the document outline."
  },
  {
    name: "paragraph",
    propsSchema: ParagraphPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Paragraph text - supports formatting like bold, italic, and color. Main content element."
  },
  {
    name: "image",
    propsSchema: ImagePropsSchema,
    hasChildren: false,
    category: "content",
    description: "Image element - displays images with optional caption. Supports various formats."
  },
  {
    name: "statistic",
    propsSchema: StatisticPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Statistic display - shows a number with description. Perfect for KPIs and metrics."
  },
  {
    name: "block",
    propsSchema: BlockInvocationPropsSchema2,
    hasChildren: false,
    category: "content",
    description: "Invoke a JSON block defined in this document\u2019s props.blocks. Fill its named slots; no block names are built into the engine."
  },
  {
    name: "group",
    propsSchema: GroupPropsSchema,
    hasChildren: true,
    allowedChildren: FLOW_CHILDREN,
    category: "container",
    description: "Transparent group of flow components; also the inspectable result of block expansion."
  },
  {
    name: "table",
    propsSchema: TablePropsSchema,
    createPropsSchema: createTablePropsSchema,
    embeddedComponents: [
      // Cell content is `string | component`. The table's own props schema
      // already validates the cell structure, so the walk only adds the
      // per-component prop errors the loose static content ref misses —
      // reporting structure here would double-report.
      {
        path: ["columns", "*", "header", "content"],
        arity: "component",
        reportStructure: false
      },
      {
        path: ["columns", "*", "cells", "*", "content"],
        arity: "component",
        reportStructure: false
      }
    ],
    hasChildren: false,
    category: "content",
    description: "Data table, declared COLUMN-MAJOR: `props.columns[]`, each with its own `header` and its own `cells[]` running down the column. Note the PPTX `table` is the other way round \u2014 rows of cells \u2014 so a table cannot be moved between the formats unchanged. Fixed column widths are points and must fit the page; leave some columns unsized so they share the leftover space."
  },
  {
    name: "list",
    propsSchema: ListPropsSchema,
    hasChildren: false,
    category: "content",
    description: "List element - bulleted or numbered list items. Supports nested lists."
  },
  {
    name: "toc",
    propsSchema: TocPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Table of contents - automatically generates TOC from document headings. Supports depth ranges and custom styles."
  },
  {
    name: "divider",
    propsSchema: DividerPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Horizontal divider - a thin line across the measure, for separating sections. Drawn as a Word paragraph border, so it stays a real Word object. Use this rather than collapsing a paragraph's line spacing to fake one."
  },
  {
    name: "highcharts",
    propsSchema: HighchartsPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Chart component powered by Highcharts - render line, bar, pie, heatmap, and more with rich options."
  },
  {
    name: "chart",
    propsSchema: ChartPropsSchema,
    hasChildren: false,
    // docx.js has no chart primitive at all, so this is a backend gap rather
    // than a slice boundary — the same reasoning that keeps `drawing-groups`
    // off the docxjs capability set.
    renderers: ["office-open"],
    category: "content",
    description: 'Native Word chart - editable, scalable, no export server needed. Requires renderer "office-open".'
  },
  {
    name: "visual",
    propsSchema: VisualPropsSchema,
    hasChildren: false,
    category: "content",
    description: 'Free-canvas graphic for infographics, diagrams and layered compositions the document flow cannot express. Rasterized from a pptx slide by default; renderMode "native" draws it as an editable Word drawing group (renderer "office-open").'
  }
];
function getStandardComponent(name) {
  return STANDARD_COMPONENTS_REGISTRY.find((c) => c.name === name);
}
function getAllStandardComponentNames() {
  return STANDARD_COMPONENTS_REGISTRY.map((c) => c.name);
}
function getComponentsByCategory(category) {
  return STANDARD_COMPONENTS_REGISTRY.filter((c) => c.category === category);
}
function getContainerComponents() {
  return STANDARD_COMPONENTS_REGISTRY.filter((c) => c.hasChildren);
}
function getContentComponents() {
  return STANDARD_COMPONENTS_REGISTRY.filter((c) => !c.hasChildren);
}
function isStandardComponent(name) {
  return STANDARD_COMPONENTS_REGISTRY.some((c) => c.name === name);
}
function demandsProps(propsSchema) {
  const schema = propsSchema;
  return schema.type !== "object" || (schema.required?.length ?? 0) > 0;
}
function createComponentSchemaObject(component, childrenType, selfRef, profile) {
  const schema = {
    name: Type8.Literal(component.name),
    id: Type8.Optional(Type8.String()),
    enabled: Type8.Optional(
      Type8.Boolean({
        default: true,
        description: "When false, this component is filtered out and not rendered. Defaults to true. Useful for conditional component inclusion."
      })
    )
  };
  if (component.special?.hasSchemaField) {
    schema.$schema = Type8.Optional(Type8.String({ format: "uri" }));
    schema.renderer = profile ? profile.requireDiscriminator ? Type8.Literal(profile.renderer, {
      description: "Renderer backend for this document"
    }) : Type8.Optional(
      Type8.Literal(profile.renderer, {
        description: 'Renderer backend. Omitted defaults to "docxjs".'
      })
    ) : Type8.Optional(
      Type8.Union(
        DOCX_RENDERER_IDS.map((renderer) => Type8.Literal(renderer)),
        {
          description: 'Renderer backend. Omitted defaults to "docxjs".'
        }
      )
    );
  }
  const basePropsSchema = component.createPropsSchema && selfRef ? component.createPropsSchema(selfRef) : component.propsSchema;
  const propsSchema = profile ? docxPropsSchemaForRenderer(
    component.name,
    basePropsSchema,
    profile.renderer
  ) : basePropsSchema;
  schema.props = demandsProps(propsSchema) ? propsSchema : Type8.Optional(propsSchema);
  if (component.hasChildren && childrenType) {
    schema.children = component.special?.hasSchemaField ? Type8.Array(childrenType) : Type8.Optional(Type8.Array(childrenType));
  }
  return Type8.Object(schema, {
    additionalProperties: false,
    description: component.description
  });
}
function createAllComponentSchemas(recursiveRef) {
  return STANDARD_COMPONENTS_REGISTRY.map(
    (component) => createComponentSchemaObject(component, recursiveRef)
  );
}
function sameNames(a, b) {
  const set = new Set(a);
  return a.length === set.size && b.length === set.size && b.every((n) => set.has(n));
}
function createAllComponentSchemasNarrowed(selfRef, pluginSchemas = [], profile) {
  const drawnHere = (comp) => !profile || !comp.renderers || comp.renderers.includes(profile.renderer);
  const components = STANDARD_COMPONENTS_REGISTRY.filter(drawnHere);
  const inFlow = new Set(FLOW_CHILDREN);
  const byName = /* @__PURE__ */ new Map();
  for (const comp of components) {
    if (!comp.hasChildren) {
      byName.set(
        comp.name,
        createComponentSchemaObject(comp, void 0, selfRef, profile)
      );
    }
  }
  const resolveContainers = (containers, flowRef) => {
    const pending = [...containers];
    while (pending.length > 0) {
      const before = pending.length;
      for (let i = pending.length - 1; i >= 0; i--) {
        const comp = pending[i];
        if (!comp.allowedChildren) {
          byName.set(
            comp.name,
            createComponentSchemaObject(comp, selfRef, selfRef, profile)
          );
          pending.splice(i, 1);
          continue;
        }
        if (sameNames(comp.allowedChildren, FLOW_CHILDREN)) {
          byName.set(
            comp.name,
            createComponentSchemaObject(comp, flowRef, selfRef, profile)
          );
          pending.splice(i, 1);
          continue;
        }
        const deps = comp.allowedChildren.filter(
          (name) => components.some((c) => c.name === name && c.hasChildren)
        );
        if (!deps.every((name) => byName.has(name))) continue;
        const childSchemas = comp.allowedChildren.map((name) => byName.get(name)).filter((s) => s !== void 0);
        const allChildSchemas = [...childSchemas, ...pluginSchemas];
        const childrenType = allChildSchemas.length === 1 ? allChildSchemas[0] : Type8.Union(allChildSchemas);
        byName.set(
          comp.name,
          createComponentSchemaObject(comp, childrenType, selfRef, profile)
        );
        pending.splice(i, 1);
      }
      if (pending.length === before) {
        throw new Error(
          `Circular allowedChildren among: ${pending.map((c) => c.name).join(", ")}`
        );
      }
    }
  };
  const flow = Type8.Recursive(
    (Flow) => {
      resolveContainers(
        components.filter((c) => c.hasChildren && inFlow.has(c.name)),
        Flow
      );
      const members = FLOW_CHILDREN.map((name) => byName.get(name)).filter(
        (s) => s !== void 0
      );
      return Type8.Union([...members, ...pluginSchemas], {
        discriminator: { propertyName: "name" },
        description: "Flow content: what a section body holds, and so what a group, columns or text-box holds."
      });
    },
    { $id: docxFlowDefinitionName(profile?.renderer) }
  );
  resolveContainers(
    components.filter((c) => c.hasChildren && !inFlow.has(c.name)),
    flow
  );
  const schemas = components.map((comp) => byName.get(comp.name)).filter((s) => s !== void 0);
  const roots = components.filter((comp) => !inFlow.has(comp.name)).map((comp) => byName.get(comp.name));
  return { schemas, byName, flow, roots };
}

export {
  GroupPropsSchema,
  BlockInvocationPropsSchema,
  createReportPropsSchema,
  ReportPropsSchema,
  TextBoxPropsSchema,
  TocStyleSchema,
  TocScopeSchema,
  TocStyleMappingSchema,
  TocDepthRangeSchema,
  TocPropsSchema,
  DividerPropsSchema,
  HighchartsPropsSchema,
  ChartPropsSchema,
  FLOW_CHILDREN,
  STANDARD_COMPONENTS_REGISTRY,
  getStandardComponent,
  getAllStandardComponentNames,
  getComponentsByCategory,
  getContainerComponents,
  getContentComponents,
  isStandardComponent,
  createComponentSchemaObject,
  createAllComponentSchemas,
  createAllComponentSchemasNarrowed
};
//# sourceMappingURL=chunk-JETULU6Z.js.map